import { useEffect, useRef, useState } from "react";
import { api, type TimeseriesResponse } from "@shared/api";

interface UseTimeseriesOptions {
  metric: string;
  bucket: "match" | "day";
  from?: number;
  to?: number;
  refreshKey?: number;
}

export function useTimeseries({ metric, bucket, from, to, refreshKey = 0 }: UseTimeseriesOptions) {
  const [data, setData] = useState<TimeseriesResponse["points"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryKeyRef = useRef<string | null>(null);
  const queryKey = `${metric}:${bucket}:${from ?? "all"}:${to ?? "all"}`;

  useEffect(() => {
    const queryChanged = queryKeyRef.current !== queryKey;
    queryKeyRef.current = queryKey;
    if (queryChanged) setData(null);

    let cancelled = false;
    setError(null);

    api
      .timeseries(metric, bucket, from, to)
      .then((response) => {
        if (cancelled) return;
        setData(response.points);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setData((current) => current ?? []);
      });

    return () => {
      cancelled = true;
    };
  }, [bucket, from, metric, queryKey, refreshKey, to]);

  return { data, error };
}
